/**
 * One exercise of an IELTS lesson as the learner sees it (read-only preview: options, blanks,
 * TRUE / FALSE / NOT GIVEN, pairs, order, writing or speaking task), followed by what only the
 * trainer sees: the expected answer, the explanation, the French help and the model answer.
 * The content is in English; the labels follow the interface language.
 */

import { type Exercise } from "@agencyos/api-client";
import { TFNG, letter, splitGaps } from "@agencyos/core";
import { Badge } from "@agencyos/ui-web";
import { Mic, PenLine } from "lucide-react";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";

import "./ielts-admin.css";
import { answerText, exerciseView, type ExerciseView } from "./ieltsData";

function GapPrompt({ view }: { view: ExerciseView }) {
  const { t } = useTranslation();
  const parts = splitGaps(view.prompt);
  return (
    <p lang="en" className="ielts-preview__prompt">
      {parts.map((part, i) => (
        <Fragment key={i}>
          {part}
          {i < parts.length - 1 ? <input className="ielts-preview__gap" disabled aria-label={t("academyAdmin.ielts.exercise.blank", { n: i + 1 })} /> : null}
        </Fragment>
      ))}
    </p>
  );
}

function LearnerView({ view }: { view: ExerciseView }) {
  const { t } = useTranslation();
  const prompt = view.type === "gap" ? <GapPrompt view={view} /> : view.prompt ? <p lang="en" className="ielts-preview__prompt">{view.prompt}</p> : null;
  return (
    <div className="ielts-preview__learner stack" lang="en">
      {view.text ? <p className="ielts-preview__text academy-pre">{view.text}</p> : null}
      {view.script ? (
        <p className="ielts-preview__text academy-pre">
          <span className="ao-footnote ao-secondary">{t("academyAdmin.ielts.exercise.script")} · </span>
          {view.script}
        </p>
      ) : null}
      {prompt}
      {view.type === "mcq" || view.type === "multi" ? (
        <ul className="ielts-preview__options">
          {view.options.map((option, i) => (
            <li key={`${option}-${i}`}>
              <span className="ielts-preview__letter" aria-hidden="true">
                {letter(i)}
              </span>
              {option}
            </li>
          ))}
        </ul>
      ) : null}
      {view.type === "multi" && view.choose ? <p className="ao-footnote ao-secondary">{t("academyAdmin.ielts.exercise.choose", { count: view.choose })}</p> : null}
      {view.type === "tfng" ? (
        <div className="ielts-chips">
          {TFNG.map((word) => (
            <span key={word} className="ielts-preview__pill">
              {word}
            </span>
          ))}
        </div>
      ) : null}
      {view.type === "match" ? (
        <div className="ielts-two">
          <ol className="ielts-preview__column">
            {view.left.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ol>
          <ul className="ielts-preview__column ielts-preview__column--letters">
            {view.right.map((item, i) => (
              <li key={item}>
                <span className="ielts-preview__letter" aria-hidden="true">
                  {String.fromCharCode(97 + i)}
                </span>
                {item}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {view.type === "order" ? (
        <ul className="ielts-chips">
          {view.items.map((item) => (
            <li key={item} className="ielts-preview__pill">
              {item}
            </li>
          ))}
        </ul>
      ) : null}
      {view.type === "short" ? <input className="ielts-preview__gap ielts-preview__gap--wide" disabled aria-label={t("academyAdmin.ielts.exercise.answerField")} /> : null}
      {view.type === "short" && view.maxWords ? <p className="ao-footnote ao-secondary">{t("academyAdmin.ielts.exercise.maxWords", { count: view.maxWords })}</p> : null}
      {view.type === "write" ? (
        <p className="ielts-preview__task">
          <PenLine size={16} strokeWidth={1.75} aria-hidden="true" /> {view.minWords ? t("academyAdmin.ielts.exercise.minWords", { count: view.minWords }) : t("academyAdmin.ielts.exercise.writeTask")}
        </p>
      ) : null}
      {view.type === "speak" ? (
        <p className="ielts-preview__task">
          <Mic size={16} strokeWidth={1.75} aria-hidden="true" /> {view.seconds ? t("academyAdmin.ielts.exercise.seconds", { count: view.seconds }) : t("academyAdmin.ielts.exercise.speakTask")}
        </p>
      ) : null}
    </div>
  );
}

export function ExercisePreview({ exercise, index }: { exercise: Exercise | Record<string, unknown>; index: number }) {
  const { t } = useTranslation();
  const view = exerciseView(exercise);
  const answer = answerText(view);
  return (
    <article className="ielts-preview ao-surface ao-card stack" aria-label={t("academyAdmin.ielts.exercise.label", { n: index })}>
      <div className="row between">
        <span className="row">
          <span className="ao-num ao-secondary">{index}.</span>
          <Badge tone="info">{t(`academyAdmin.ielts.exercise.type.${view.type}`, { defaultValue: view.type })}</Badge>
          {view.type === "write" || view.type === "speak" ? <Badge tone="offer">{t("academyAdmin.ielts.exercise.aiGraded")}</Badge> : null}
        </span>
        <span className="ao-footnote ao-secondary">{t("academyAdmin.ielts.exercise.learnerView")}</span>
      </div>
      <LearnerView view={view} />
      <dl className="ielts-preview__key">
        {answer ? (
          <div>
            <dt>{t("academyAdmin.ielts.exercise.expected")}</dt>
            <dd lang="en">{answer}</dd>
          </div>
        ) : null}
        {view.explanation ? (
          <div>
            <dt>{t("academyAdmin.ielts.exercise.explanation")}</dt>
            <dd lang="en">{view.explanation}</dd>
          </div>
        ) : null}
        {view.helpFr ? (
          <div>
            <dt>{t("academyAdmin.ielts.exercise.helpFr")}</dt>
            <dd lang="fr">{view.helpFr}</dd>
          </div>
        ) : null}
        {view.modelAnswer ? (
          <div>
            <dt>{t("academyAdmin.ielts.exercise.modelAnswer")}</dt>
            <dd lang="en" className="academy-pre">
              {view.modelAnswer}
            </dd>
          </div>
        ) : null}
      </dl>
    </article>
  );
}
